





































































































import { PropType } from 'vue';
import { mapActions } from 'vuex';
import moment from 'moment';
import { AxiosError } from 'axios';

import {
  IWorkflowFormFieldSubmissionData,
  IWorkflowFormSubmission,
  IWorkflowFormTemplateRevision,
  IWorkflowFormSectionFieldData,
  IWorkflowFormFieldData,
  IWorkflowFormSubmissionCommentData
} from '@/models';
import { ISystemError } from '@/models/error';
import { isHtmlTextEmpty, setErrorFromAxiosError } from '@/utils/helpers';

import SnackBarMixin from '@/mixins/snackbar';
import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';

import WorkflowFormSubmissionDialog from './WorkflowFormSubmissionDialog.vue';
import WorkflowFormSubmissionStateActions from './WorkflowFormSubmissionStateActions';
import WorkflowFormSubmissionCommentField from './WorkflowFormSubmissionCommentField.vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';

const baseMixins = mixins(SnackBarMixin, auth);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $refs: {
    workflowFormSubmissionDialog: InstanceType<
      typeof WorkflowFormSubmissionDialog
    >;
    workflowFormSubmissionCommentField: InstanceType<
      typeof WorkflowFormSubmissionCommentField
    >;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: {
      ConfirmationDialog,
      WorkflowFormSubmissionDialog,
      WorkflowFormSubmissionStateActions,
      WorkflowFormSubmissionCommentField
    },

    props: {
      submission: {
        type: Object as PropType<IWorkflowFormSubmission>
      },
      loading: {
        type: Boolean
      },
      isOwner: Boolean,
      revision: Object as PropType<IWorkflowFormTemplateRevision>
    },

    data() {
      return {
        error: null as ISystemError | null,
        comment: {
          text: ''
        } as IWorkflowFormSubmissionCommentData,
        commentError: null as ISystemError | null,
        commentLoading: false,
        isCommentDialogActive: false
      };
    },

    computed: {
      internalLoading: {
        get(): boolean {
          return this.loading;
        },
        set(value: boolean) {
          this.$emit('update:loading', value);
        }
      },

      isCommentValid(): boolean {
        return !isHtmlTextEmpty(this.comment.text) && !this.commentLoading;
      }
    },

    methods: {
      moment,

      ...mapActions({
        createWorkflowFormSubmissionComment:
          'workflowForm/createWorkflowFormSubmissionComment',
        addWorkflowFormSubmissionCommentAttachments:
          'workflowForm/addWorkflowFormSubmissionCommentAttachments'
      }),

      openFormDialog() {
        this.$refs.workflowFormSubmissionDialog.open(
          this.revision,
          this.submission
        );
      },

      getWorkflowFormSubmissionValueByMetaLabel(
        meta_label: string
      ): string | undefined {
        const formFieldSubmission = (this.submission
          .answers as IWorkflowFormFieldSubmissionData[]).find(
          form =>
            ((form.field as IWorkflowFormSectionFieldData)
              .field_template as IWorkflowFormFieldData).meta_label ===
            meta_label
        );

        if (!formFieldSubmission) {
          return;
        }

        return formFieldSubmission.value;
      },

      refreshChecklist() {
        this.$emit('refreshChecklist');
      },

      openTransitionCommentDialog() {
        this.comment = {
          text: ''
        };
        this.isCommentDialogActive = true;
      },

      closeTransitionCommentDialog() {
        // Reset comment field and attachments data once comment is saved or dialog is closed
        this.$refs.workflowFormSubmissionCommentField.reset();
        this.isCommentDialogActive = false;
      },

      clearCommentError() {
        this.commentError = null;
      },

      async addSubmissionComment() {
        try {
          this.clearCommentError();

          const {
            comment,
            attachments
          } = this.$refs.workflowFormSubmissionCommentField.addComment();

          this.commentLoading = true;
          const commentId = (await this.createWorkflowFormSubmissionComment(
            comment
          )) as Promise<number>;

          if (attachments.length !== 0) {
            await this.addWorkflowFormSubmissionCommentAttachments({
              submissionId: comment.submissionId,
              commentId: commentId,
              attachments: attachments
            });
          }

          this.closeTransitionCommentDialog();
        } catch (error) {
          this.commentError = setErrorFromAxiosError(error as AxiosError);
        } finally {
          this.commentLoading = false;
        }
      }
    }
  });
